import React, { useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { editorStateToHtml, htmlToEditorState } from "./richTextRoundtrip";

export interface ReadingBlockRichTextProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const hexToRgb = (hex: string): string => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgb(${r},${g},${b})`;
};

type ToolbarComponentProps = {
  onChange: (key: string, value: string) => void;
  currentState?: { color?: string };
};

const CustomColorPicker: React.FC<ToolbarComponentProps> = ({
  onChange,
  currentState,
}) => {
  const handlePick = (hex: string) => {
    onChange("color", hexToRgb(hex));
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0 4px",
        height: 30,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <label
        title="Color del texto"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          cursor: "pointer",
          fontSize: 12,
          color: "#555",
        }}
      >
        <span
          style={{
            fontWeight: 700,
            color: currentState?.color || "#000",
            borderBottom: `3px solid ${currentState?.color || "#000"}`,
            lineHeight: 1,
            padding: "0 2px",
          }}
        >
          A
        </span>
        <input
          type="color"
          onChange={(e) => handlePick(e.target.value)}
          style={{
            width: 22,
            height: 22,
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 0,
            cursor: "pointer",
            background: "transparent",
          }}
        />
      </label>
    </div>
  );
};

const TOOLBAR_CONFIG = {
  options: ["inline", "list", "fontSize", "colorPicker"],
  inline: {
    options: ["bold", "italic", "underline"],
  },
  list: {
    options: ["unordered", "ordered"],
  },
  fontSize: {
    options: [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72],
  },
  colorPicker: {
    component: CustomColorPicker,
  },
};

const ReadingBlockRichText: React.FC<ReadingBlockRichTextProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const lastEmittedHtmlRef = useRef<string>("");
  const [editorState, setEditorState] = useState<EditorState>(() =>
    htmlToEditorState(value || ""),
  );

  useEffect(() => {
    if (value === lastEmittedHtmlRef.current) return;
    setEditorState(htmlToEditorState(value || ""));
  }, [value]);

  const handleChange = (next: EditorState) => {
    setEditorState(next);
    const html = editorStateToHtml(next);
    lastEmittedHtmlRef.current = html;
    onChange(html);
  };

  return (
    <div className="reading-block-rich-text">
      <Editor
        editorState={editorState}
        onEditorStateChange={handleChange}
        toolbar={TOOLBAR_CONFIG}
        placeholder={placeholder}
        localization={{ locale: "es" }}
        toolbarClassName="reading-block-rich-text__toolbar"
        wrapperClassName="reading-block-rich-text__wrapper"
        editorClassName="reading-block-rich-text__editor"
      />
    </div>
  );
};

export default ReadingBlockRichText;
